import { PropsWithChildren, ReactElement, createContext, use } from 'react';

import { Styles, css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns/hstack';

import { useControllableState } from '../../misc/state/use-controllable-state';
import { ChoiceChip, ChoiceChipProps, ChoiceChipVariantType } from './choice';

interface ChipGroupProps {
  css?: Styles;
}

const ChipGroup = ({ css: cssProps, children }: PropsWithChildren<ChipGroupProps>) => {
  return <div className={css(hstack.raw({ gap: 8 }), cssProps)}>{children}</div>;
};

// chip group for single select purpose
// https://prextech.slack.com/archives/C073YCY3708/p1752203642376649?thread_ts=1752122348.707709&cid=C073YCY3708
interface SingleChoiceContext<T extends string> {
  type?: ChoiceChipVariantType;
  selected?: T;
  setSelected: (value: T) => void;
}
const SingleChoiceContext = createContext<SingleChoiceContext<string>>(null!);
interface SingleChoiceGroupProps<T> {
  type?: ChoiceChipVariantType;
  defaultValue?: T;
  value?: T;
  onChange?: (value: T) => void;
  css?: Styles;
  children: React.ReactNode;
}
const SingleChoiceGroup = <T extends string>({
  type,
  defaultValue,
  value,
  onChange,
  css: cssProps,
  children,
}: SingleChoiceGroupProps<T>) => {
  const [selected, setSelected] = useControllableState<T>({ defaultValue, value, onChange });
  return (
    <SingleChoiceContext value={{ type, selected, setSelected } as SingleChoiceContext<string>}>
      <div className={css(hstack.raw({ gap: 8 }), cssProps)}>{children}</div>
    </SingleChoiceContext>
  );
};
interface SingleChoiceChipProps<T> extends Omit<ChoiceChipProps, 'selected' | 'type'> {
  value: T;
}
const SingleChoiceChip = <T extends string>({ value, onClick, ...props }: SingleChoiceChipProps<T>) => {
  const { type, selected, setSelected } = use(SingleChoiceContext);
  return (
    <ChoiceChip
      {...props}
      type={type}
      selected={value === selected}
      onClick={() => {
        setSelected(value);
        onClick?.();
      }}
    />
  );
};

type SingleChoice<T extends string> = {
  (props: SingleChoiceGroupProps<T>): ReactElement;
  Chip: (props: SingleChoiceChipProps<T>) => ReactElement;
};
const SingleChoice = Object.assign(SingleChoiceGroup, {
  Chip: SingleChoiceChip,
});
export function createSingleChoice<T extends string>(): SingleChoice<T> {
  return SingleChoice as unknown as SingleChoice<T>;
}

export const Chip = {
  Group: ChipGroup,
  Choice: ChoiceChip,
};
